import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Badge,
  Fab,
  Drawer,
  Typography,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search,
  QueueMusic,
  Inbox,
  CardGiftcard as PrizeIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import Sidebar from "./Sidebar";
import RightSidebar from "./RightSidebar";
import GigHomeTab from "./GigHomeTab";
import SearchTab from "./SearchTab";
import QueueTab from "./QueueTab";
import SongqRequests from "../SongqRequests";
import PrivatePlaylistTab from "../PrivatePlaylistTab";
import FlashQuizManagement from "../quiz/FlashQuizManagement";
import HigherOrLowerDashboard from "../games/HigherOrLowerDashboard";
import BeerillionerDashboard from "../games/BeerillionerDashboard";
import SpinningWheelDashboard from "../games/SpinningWheelDashboard";
import PrizePalette from "../prizes/PrizePalette";
import PrizeLiveAward from "../prizes/PrizeLiveAward";
import io from "socket.io-client";
import { guardedNavigate } from "../../../utils/adminNavigationGuard";

const HOL_HOST_TABS = new Set(["higher-lower-pwh", "higher-lower-single-player"]);

const TAB_CONFIG = [
  { id: "search", label: "Search Songs", icon: Search },
  { id: "queue", label: "Queue", icon: QueueMusic },
  // { id: "playlist", label: "Private Playlist", icon: LibraryMusic },
  { id: "requests", label: "Requests", icon: Inbox },
  // { id: "quiz-config", label: "Quiz Config", icon: Settings },
];

const GigLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(!isMobile);
  const [activeTab, setActiveTab] = useState("home");
  const [queue, setQueue] = useState([]);
  const [songQCount, setSongQCount] = useState(0);
  const [liveAwardOpen, setLiveAwardOpen] = useState(false);
  const socketRef = useRef(null);

  const activeTabIndex = TAB_CONFIG.findIndex((t) => t.id === activeTab);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/gig-queue`
      );
      const data = await res.json();
      setQueue(data);
    } catch (err) {
      console.error("Error fetching queue:", err);
    }
  }, []);

  const fetchSongQCount = useCallback(async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/song-requests`
      );
      if (res.ok) {
        const data = await res.json();
        const now = new Date();
        const songQRequests = data.filter((req) => {
          if (req.status === "Fulfilled") return false;
          const expiresAt = new Date(req.expires_at);
          return now < expiresAt;
        });
        setSongQCount(songQRequests.length);
      }
    } catch (err) {
      console.error("Error fetching song-q count:", err);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    fetchSongQCount();

    const socket = io(process.env.REACT_APP_API_URL);
    socketRef.current = socket;
    socket.emit("gig:join");

    socket.on("gig:queue-updated", (updatedQueue) => {
      setQueue(updatedQueue);
    });

    socket.on("new-song-request", () => {
      fetchSongQCount();
    });

    socket.on("song-request-updated", () => {
      fetchSongQCount();
    });

    return () => {
      socket.emit("gig:leave");
      socket.disconnect();
    };
  }, [fetchQueue, fetchSongQCount]);

  const handleAddToQueue = async (songsMasterId) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/gig-queue`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ songs_master_id: songsMasterId }),
      }
    );
    if (!res.ok) throw new Error("Failed to add to queue");
    const data = await res.json();
    setQueue(data.queue);
  };

  const handleRemoveFromQueue = async (gigQueueId) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/gig-queue/${gigQueueId}`,
      { method: "DELETE" }
    );
    if (!res.ok) throw new Error("Failed to remove from queue");
    const data = await res.json();
    setQueue(data.queue);
  };

  const handleReorderQueue = async (orderedIds) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/gig-queue/reorder`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderedIds }),
      }
    );
    if (!res.ok) throw new Error("Failed to reorder queue");
    const data = await res.json();
    setQueue(data.queue);
  };

  const handleClearQueue = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/gig-queue/clear/all`,
      { method: "DELETE" }
    );
    if (!res.ok) throw new Error("Failed to clear queue");
    setQueue([]);
  };

  const flashQuizRef = useRef(null);

  const changeTab = useCallback(
    (tabId, afterChange) => {
      const sameMountedView =
        tabId === activeTab || (HOL_HOST_TABS.has(tabId) && HOL_HOST_TABS.has(activeTab));
      const go = () => {
        setActiveTab(tabId);
        afterChange?.();
      };
      if (sameMountedView) go();
      else guardedNavigate(go);
    },
    [activeTab]
  );

  const handleTabChange = (_, newValue) => {
    changeTab(TAB_CONFIG[newValue].id);
  };

  // Quiz sidebar action handlers
  const openQuizConfig = (method, session) =>
    changeTab("quiz-config", () => setTimeout(() => flashQuizRef.current?.[method]?.(session), 100));

  const handleViewQuizDetails = (session) => openQuizConfig("viewQuizDetails", session);

  const handleEditQuizSession = (session) => openQuizConfig("editQuizSession", session);

  const handleViewParticipants = (session) => openQuizConfig("viewParticipants", session);

  const handleViewQuizletRankings = (session) => openQuizConfig("viewQuizletRankings", session);

  const handleEndFlashQuizSession = async (sessionId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/live-quiz/flash/sessions/${sessionId}/end`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        }
      );
      if (!res.ok) throw new Error("Failed to end session");
    } catch (err) {
      console.error("Error ending flash quiz session:", err);
    }
  };

  const handleToggleSessionStatus = async (sessionId, currentStatus) => {
    try {
      const action = currentStatus === "active" ? "deactivate" : "activate";
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/live-quiz/sessions/${sessionId}/${action}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        }
      );
      if (!res.ok) throw new Error(`Failed to ${action} session`);
    } catch (err) {
      console.error("Error toggling session status:", err);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 64px)",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#121210",
      }}
    >
      <Sidebar
        open={sidebarOpen}
        onToggle={() => setSidebarOpen((p) => !p)}
        activeTab={activeTab}
        onTabChange={changeTab}
        queueCount={queue.length}
        requestCount={songQCount}
        onViewQuizDetails={handleViewQuizDetails}
        onEditQuizSession={handleEditQuizSession}
        onViewParticipants={handleViewParticipants}
        onViewQuizletRankings={handleViewQuizletRankings}
        onEndFlashQuizSession={handleEndFlashQuizSession}
        onToggleSessionStatus={handleToggleSessionStatus}
      />

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Tab bar at top */}
        <Box
          sx={{
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            backgroundColor: "#141414",
            minHeight: 44,
          }}
        >
          {(isMobile || !sidebarOpen) && (
            <IconButton
              onClick={() => setSidebarOpen(true)}
              sx={{ color: "rgba(255,255,255,0.6)", ml: 1 }}
              size="small"
            >
              <MenuIcon />
            </IconButton>
          )}
          <Tabs
            value={activeTabIndex >= 0 ? activeTabIndex : false}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            sx={{
              flex: 1,
              minHeight: 44,
              "& .MuiTab-root": {
                minHeight: 44,
                textTransform: "none",
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "rgba(255,255,255,0.45)",
                px: 2,
                "&.Mui-selected": {
                  color: "#1DB954",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1DB954",
                height: 2,
              },
            }}
          >

            {TAB_CONFIG.map((tab) => {
              const Icon = tab.icon;
              const badgeCount =
                tab.id === "queue"
                  ? queue.length
                  : tab.id === "requests"
                  ? songQCount
                  : 0;
              return (
                <Tab
                  key={tab.id}
                  icon={
                    badgeCount > 0 ? (
                      <Badge
                        badgeContent={badgeCount}
                        color={tab.id === "requests" ? "warning" : "success"}
                        max={99}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "0.55rem",
                            height: 14,
                            minWidth: 14,
                            backgroundColor:
                              tab.id === "requests" ? "#FFA726" : "#1DB954",
                          },
                        }}
                      >
                        <Icon sx={{ fontSize: 18 }} />
                      </Badge>
                    ) : (
                      <Icon sx={{ fontSize: 18 }} />
                    )
                  }
                  iconPosition="start"
                  label={tab.label}
                  sx={{ gap: 0.75 }}
                />
              );
            })}
          </Tabs>
          <IconButton
            onClick={() => setRightSidebarOpen((p) => !p)}
            size="small"
            sx={{
              color: rightSidebarOpen
                ? "#1DB954"
                : "rgba(255, 245, 245, 0.4)",
              mr: 1,
            }}
          >
            <MenuIcon sx={{ fontSize: "1.5rem" }} />
          </IconButton>
        </Box>

        {/* Tab content */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {activeTab === "home" && <GigHomeTab />}
          {activeTab === "search" && (
            <SearchTab onAddToQueue={handleAddToQueue} />
          )}
          {activeTab === "queue" && (
            <QueueTab
              queue={queue}
              onRemove={handleRemoveFromQueue}
              onReorder={handleReorderQueue}
              onClear={handleClearQueue}
            />
          )}
          {activeTab === "playlist" && <PrivatePlaylistTab />}
          {activeTab === "requests" && <SongqRequests />}
          {activeTab === "quiz-config" && <FlashQuizManagement ref={flashQuizRef} />}
          {(activeTab === "higher-lower" ||
            activeTab === "higher-lower-offline" ||
            activeTab === "higher-lower-analytics") && (
            <HigherOrLowerDashboard
              showHeader={false}
              initialMode={0}
              showModeTabs={false}
            />
          )}
          {activeTab === "higher-lower-config" && (
            <HigherOrLowerDashboard
              showHeader={false}
              initialMode={1}
              showModeTabs={false}
            />
          )}
          {(activeTab === "higher-lower-single-player" ||
            activeTab === "higher-lower-pwh") && (
            <HigherOrLowerDashboard
              showHeader={false}
              initialMode={2}
              showModeTabs={false}
            />
          )}
          {activeTab === "spin-wheel-normal" && (
            <SpinningWheelDashboard
              initialTab={1}
              showTabNavigation={false}
              showHeader={false}
            />
          )}
          {activeTab === "spin-wheel-live-host" && (
            <SpinningWheelDashboard
              initialTab={0}
              showTabNavigation={false}
              showHeader={false}
            />
          )}
          {activeTab === "spin-wheel-hot-seat" && (
            <SpinningWheelDashboard
              initialTab={2}
              showTabNavigation={false}
              showHeader={false}
            />
          )}
          {activeTab === "beerillioner-config" && (
            <BeerillionerDashboard
              initialTab={0}
              showTabNavigation={false}
              showHeader={false}
            />
          )}
          {activeTab === "beerillioner-host" && (
            <BeerillionerDashboard
              initialTab={1}
              showTabNavigation={false}
              showHeader={false}
            />
          )}
          {activeTab === "beerillioner-history" && (
            <BeerillionerDashboard
              initialTab={2}
              showTabNavigation={false}
              showHeader={false}
            />
          )}
          {activeTab === "prize-palette" && (
            <PrizePalette showHeader={false} embedded initialTab={0} />
          )}
        </Box>
      </Box>

      <RightSidebar
        open={rightSidebarOpen}
        onToggle={() => setRightSidebarOpen((p) => !p)}
      />

      {/* Floating Live Award shortcut */}
      {!liveAwardOpen && activeTab !== "prize-palette" && (
        <Fab
          color="primary"
          aria-label="Open Live Award"
          onClick={() => setLiveAwardOpen(true)}
          sx={{
            position: "fixed",
            right: { xs: 16, md: rightSidebarOpen ? 300 : 24 },
            bottom: 24,
            zIndex: 1300,
            bgcolor: "#FFD54F",
            color: "#111",
            boxShadow: "0 8px 28px rgba(255,213,79,0.35)",
            transition: "right 0.3s ease, transform 0.2s ease",
            "&:hover": {
              bgcolor: "#FFC107",
              transform: "scale(1.05)",
            },
          }}
        >
          <PrizeIcon />
        </Fab>
      )}

      <Drawer
        anchor="right"
        open={liveAwardOpen}
        onClose={() => setLiveAwardOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "min(720px, 100vw)" },
            bgcolor: "#0e0e0c",
            color: "#fff",
            borderLeft: "1px solid #2a2a2a",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #2a2a2a",
            position: "sticky",
            top: 0,
            bgcolor: "#0e0e0c",
            zIndex: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PrizeIcon sx={{ color: "#FFD54F" }} />
            <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
              Live Award
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setLiveAwardOpen(false)}
            sx={{ color: "rgba(255,255,255,0.6)" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, overflow: "auto", height: "100%" }}>
          {liveAwardOpen ? <PrizeLiveAward /> : null}
        </Box>
      </Drawer>
    </Box>
  );
};

export default GigLayout;
